/**
 * Transformers to normalize Relay-style connection responses to a unified shape.
 * The API returns { data, pageInfo } so components can use cursor pagination.
 */
import type { ResultOf } from '@graphql-typed-document-node/core';

import type {
  getEnvironmentDeploymentsQuery,
  getEnvironmentsQuery,
} from './environments.queries';

type GetEnvironmentsResponse = ResultOf<typeof getEnvironmentsQuery>;
type GetEnvironmentDeploymentsResponse = ResultOf<
  typeof getEnvironmentDeploymentsQuery
>;

export type EnvironmentDeploymentNode =
  GetEnvironmentDeploymentsResponse['flyEnvironments']['searchEnvironmentDeployments']['edges'][number]['node'];

/** Search string for client-side filtering (mirrors `gqlEnvironmentsService.getEnvironmentDeployments` transform). */
export function buildEnvironmentDeploymentSearchText(
  node: EnvironmentDeploymentNode
): string {
  return [
    node.cumulativeTitle,
    node.currentDeployment.name,
    node.currentDeployment.number,
  ]
    .filter(Boolean)
    .map((s) => s?.trim())
    .join(' ');
}

/** Grid row from `GetFlyEnvironments` after `transformEnvironments` (includes `deploymentDeltas`). */
export type GetEnvironmentsGridNode =
  GetEnvironmentsResponse['flyEnvironments']['searchEnvironments']['edges'][number]['node'];

export type GetEnvironmentsPageInfo = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  endCursor: string | null;
  startCursor: string | null;
};

export type GetEnvironmentDeploymentsPageInfo = GetEnvironmentsPageInfo;

export const transformEnvironments = (
  response: GetEnvironmentsResponse
): {
  data: GetEnvironmentsGridNode[];
  pageInfo: GetEnvironmentsPageInfo;
} => {
  const connection = response?.flyEnvironments?.searchEnvironments;
  if (!connection?.edges) {
    return {
      data: [],
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
        endCursor: null,
        startCursor: null,
      },
    };
  }

  const data = connection.edges.map((edge) => {
    const node = {
      ...edge.node,
      deploymentDeltas: edge.node.deploymentDeltas ?? [],
    };
    return node;
  });
  const pi = connection.pageInfo;
  return {
    data,
    pageInfo: {
      hasNextPage: pi?.hasNextPage ?? false,
      hasPreviousPage: pi?.hasPreviousPage ?? false,
      endCursor: pi?.endCursor ?? null,
      startCursor: pi?.startCursor ?? null,
    },
  };
};

export const transformEnvironmentDeployments = (
  response: GetEnvironmentDeploymentsResponse
) => {
  const connection = response?.flyEnvironments?.searchEnvironmentDeployments;
  if (!connection?.edges) {
    return {
      deployments: [],
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
        endCursor: null,
        startCursor: null,
      },
    };
  }

  return {
    deployments: connection.edges.map((edge) => edge.node),
    pageInfo: {
      hasNextPage: connection.pageInfo?.hasNextPage ?? false,
      hasPreviousPage: connection.pageInfo?.hasPreviousPage ?? false,
      endCursor: connection.pageInfo?.endCursor ?? null,
      startCursor: connection.pageInfo?.startCursor ?? null,
    },
  };
};
